import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { onValue, ref, set } from 'firebase/database';
import { database } from '../../database/config';
import AddProductModal, {
  AddProductPayload,
} from '../components/AddProductModal';
import { PRICE_ON_REQUEST } from '../components/ProductForm';
import EditProductModal, {
  EditProductPayload,
} from '../components/EditProductModal';
import '../styles/admin.css';

type ToolItem = {
  id?: string | number;
  title?: string;
  price?: number | string;
  image?: string;
  brand?: string;
  article?: string | number;
  weight?: string | number;
  steps?: string | number;
  sizes?: (string | number)[];
  type?: string;
};

type LestnicyCategory = Record<string, ToolItem[]>;

const CATEGORY_LABELS: Record<string, string> = {
  lestnicy: 'Лестницы',
  stremyanki: 'Стремянки',
  podmosty: 'Подмости и вышки тура',
  transformery: 'Трансформеры',
  prinodlezhnosti: 'Принадлежности',
};

const CATEGORY_ORDER = [
  'lestnicy',
  'stremyanki',
  'podmosty',
  'transformery',
  'prinodlezhnosti',
];

function AdminDashboard() {
  const [groupedItems, setGroupedItems] = useState<LestnicyCategory>({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSavingProduct, setIsSavingProduct] = useState(false);
  const [editingContext, setEditingContext] = useState<{
    sourceSubcategory: string;
    item: ToolItem;
  } | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);

  useEffect(() => {
    const rootRef = ref(database);

    const unsubscribe = onValue(
      rootRef,
      (snapshot) => {
        const data = snapshot.val() as { lestnicy?: LestnicyCategory } | null;
        const lestnicy = data?.lestnicy;

        if (!lestnicy || typeof lestnicy !== 'object') {
          setGroupedItems({});
          setLoadError('');
          setIsLoading(false);
          return;
        }

        const normalized: LestnicyCategory = {};
        Object.entries(lestnicy).forEach(([key, subcategory]) => {
          normalized[key] = Array.isArray(subcategory) ? subcategory : [];
        });

        setGroupedItems(normalized);
        setLoadError('');
        setIsLoading(false);
      },
      () => {
        setLoadError('Не удалось загрузить товары категории "lestnicy".');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const sortedCategoryKeys = useMemo(() => {
    const existingKeys = Object.keys(groupedItems);
    const orderedKnown = CATEGORY_ORDER.filter((key) =>
      existingKeys.includes(key)
    );
    const unknown = existingKeys.filter((key) => !CATEGORY_ORDER.includes(key));
    return [...orderedKnown, ...unknown];
  }, [groupedItems]);

  const filteredGroupedItems = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return groupedItems;
    }

    const result: LestnicyCategory = {};
    Object.entries(groupedItems).forEach(([key, items]) => {
      result[key] = items.filter((item) => {
        const title = String(item.title ?? '').toLowerCase();
        const article = String(item.article ?? '').toLowerCase();
        return title.includes(query) || article.includes(query);
      });
    });
    return result;
  }, [groupedItems, searchTerm]);

  const visibleCategoryKeys = useMemo(
    () =>
      sortedCategoryKeys.filter(
        (key) => (filteredGroupedItems[key] ?? []).length > 0
      ),
    [sortedCategoryKeys, filteredGroupedItems]
  );

  const allItems = useMemo(
    () =>
      visibleCategoryKeys.flatMap((key) =>
        Array.isArray(filteredGroupedItems[key]) ? filteredGroupedItems[key] : []
      ),
    [filteredGroupedItems, visibleCategoryKeys]
  );

  const totalInStock = useMemo(
    () => allItems.filter((item) => getPriceNumber(item.price) > 0).length,
    [allItems]
  );

  function getPriceNumber(price: ToolItem['price']): number {
    if (price === null || price === undefined) {
      return 0;
    }
    const normalized = String(price).replace(',', '.').replace(/\s+/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function formatPrice(price: ToolItem['price']): string {
    if (String(price ?? '').trim() === PRICE_ON_REQUEST) {
      return 'Уточняйте';
    }
    const numericPrice = getPriceNumber(price);
    if (numericPrice <= 0) {
      return 'Нет в наличии';
    }
    return `${price} BYN`;
  }

  function formatWeight(weight: ToolItem['weight']): string {
    if (weight === null || weight === undefined || weight === '') {
      return '—';
    }
    return `${weight} кг`;
  }

  function handleAddClick() {
    setIsAddModalOpen(true);
  }

  function findItemIndex(items: ToolItem[], baseItem: ToolItem): number {
    if (baseItem.id !== undefined && baseItem.id !== null) {
      return items.findIndex((candidate) => candidate.id === baseItem.id);
    }
    return items.findIndex(
      (candidate) =>
        String(candidate.article ?? '') === String(baseItem.article ?? '') &&
        String(candidate.title ?? '') === String(baseItem.title ?? '')
    );
  }

  async function handleAddProduct(payload: AddProductPayload) {
    const subcategory = payload.subcategory;
    const currentItems = groupedItems[subcategory] ?? [];
    const newItem: ToolItem = {
      id:
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : String(Date.now()),
      title: payload.title.trim(),
      brand: payload.brand.trim(),
      article: payload.article.trim(),
      weight: payload.weight.trim(),
      steps: payload.steps.trim(),
      price: payload.price.trim(),
      image: payload.image.trim(),
      sizes: (payload.sizes ?? []).filter((s) => s.trim() !== ''),
      type: payload.type,
    };

    setIsSavingProduct(true);
    try {
      await set(ref(database, `lestnicy/${subcategory}`), [...currentItems, newItem]);
      message.success('Товар добавлен.');
      setIsAddModalOpen(false);
    } catch {
      message.error('Не удалось добавить товар.');
    } finally {
      setIsSavingProduct(false);
    }
  }

  function handleEditClick(item: ToolItem, sourceSubcategory: string) {
    setEditingContext({ item, sourceSubcategory });
    setIsEditModalOpen(true);
  }

  async function handleEditProduct(payload: EditProductPayload) {
    if (!editingContext) {
      return;
    }

    const { item: baseItem, sourceSubcategory } = editingContext;
    const sourceItems = [...(groupedItems[sourceSubcategory] ?? [])];
    const sourceIndex = findItemIndex(sourceItems, baseItem);

    if (sourceIndex < 0) {
      message.error('Не удалось найти товар для редактирования.');
      return;
    }

    const updatedItem: ToolItem = {
      ...sourceItems[sourceIndex],
      title: payload.title.trim(),
      brand: payload.brand.trim(),
      article: payload.article.trim(),
      weight: payload.weight.trim(),
      steps: payload.steps.trim(),
      price: payload.price.trim(),
      image: payload.image.trim(),
      sizes: (payload.sizes ?? []).filter((s) => s.trim() !== ''),
      type: payload.type,
    };

    const targetSubcategory = payload.subcategory;
    setIsUpdatingProduct(true);
    try {
      if (targetSubcategory === sourceSubcategory) {
        sourceItems[sourceIndex] = updatedItem;
        await set(ref(database, `lestnicy/${sourceSubcategory}`), sourceItems);
      } else {
        const targetItems = [...(groupedItems[targetSubcategory] ?? [])];
        sourceItems.splice(sourceIndex, 1);
        targetItems.push(updatedItem);
        await Promise.all([
          set(ref(database, `lestnicy/${sourceSubcategory}`), sourceItems),
          set(ref(database, `lestnicy/${targetSubcategory}`), targetItems),
        ]);
      }
      message.success('Товар обновлен.');
      setIsEditModalOpen(false);
      setEditingContext(null);
    } catch {
      message.error('Не удалось обновить товар.');
    } finally {
      setIsUpdatingProduct(false);
    }
  }

  async function handleDeleteProduct(item: ToolItem, sourceSubcategory: string) {
    const sourceItems = [...(groupedItems[sourceSubcategory] ?? [])];
    const sourceIndex = findItemIndex(sourceItems, item);

    if (sourceIndex < 0) {
      message.error('Не удалось найти товар для удаления.');
      return;
    }

    sourceItems.splice(sourceIndex, 1);
    try {
      await set(ref(database, `lestnicy/${sourceSubcategory}`), sourceItems);
      message.success('Товар удален.');
    } catch {
      message.error('Не удалось удалить товар.');
    }
  }

  const getColumns = (sourceSubcategory: string) => [
    {
      title: 'Фото',
      dataIndex: 'image',
      key: 'image',
      width: 72,
      render: (image: ToolItem['image'], record: ToolItem) => (
        <img
          className="admin-item-image"
          src={image || '/images/placeholder.png'}
          alt={record.title ?? 'Товар'}
        />
      ),
    },
    {
      title: 'Название',
      dataIndex: 'title',
      key: 'title',
      width: 320,
      render: (value: ToolItem['title']) => value ?? 'Без названия',
    },
    {
      title: 'Бренд',
      dataIndex: 'brand',
      key: 'brand',
      width: 140,
      render: (value: ToolItem['brand']) => value || '—',
    },
    {
      title: 'Артикул',
      dataIndex: 'article',
      key: 'article',
      width: 140,
      render: (value: ToolItem['article']) => value ?? '—',
    },
    {
      title: 'Вес',
      dataIndex: 'weight',
      key: 'weight',
      width: 110,
      render: (value: ToolItem['weight']) => formatWeight(value),
    },
    {
      title: 'Ступени',
      dataIndex: 'steps',
      key: 'steps',
      width: 90,
      render: (value: ToolItem['steps']) => value ?? '—',
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      width: 140,
      render: (value: ToolItem['price']) => (
        <Typography.Text strong>{formatPrice(value)}</Typography.Text>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 260,
      fixed: 'right' as const,
      render: (_: unknown, record: ToolItem) => (
        <Space size={8}>
          <Button onClick={() => handleEditClick(record, sourceSubcategory)}>
            Редактировать
          </Button>
          <Popconfirm
            title="Удалить товар?"
            description="Это действие нельзя отменить."
            okText="Удалить"
            cancelText="Отмена"
            onConfirm={() => handleDeleteProduct(record, sourceSubcategory)}
          >
            <Button danger>Удалить</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-dashboard-container">
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Главная админки
        </Typography.Title>
        <Typography.Text type="secondary">
          Добро пожаловать в панель управления.
        </Typography.Text>

        {loadError && <Alert type="error" showIcon message={loadError} />}

        <Card
          title='Товары категории "lestnicy"'
          extra={
            <Space>
              <Tag color="blue">Всего: {allItems.length}</Tag>
              <Button type="primary" onClick={handleAddClick}>
                Добавить товар
              </Button>
            </Space>
          }
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              В наличии: {totalInStock}
            </Typography.Text>
            <Input
              allowClear
              placeholder="Фильтр по названию или артикулу"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {isLoading ? (
              <Table
                className="admin-dashboard-table"
                loading
                columns={getColumns('lestnicy')}
                dataSource={[]}
                pagination={false}
              />
            ) : visibleCategoryKeys.length === 0 ? (
              <Typography.Text type="secondary">
                По вашему фильтру ничего не найдено.
              </Typography.Text>
            ) : (
              visibleCategoryKeys.map((categoryKey) => {
                const items = filteredGroupedItems[categoryKey] ?? [];
                return (
                  <Card
                    key={categoryKey}
                    className="admin-category-card"
                    size="small"
                    title={CATEGORY_LABELS[categoryKey] ?? categoryKey}
                    extra={<Tag>Товаров: {items.length}</Tag>}
                  >
                    <Table
                      className="admin-dashboard-table"
                      columns={getColumns(categoryKey)}
                      dataSource={items}
                      pagination={false}
                      rowKey={(item, index) =>
                        String(item.id ?? item.article ?? item.title ?? index)
                      }
                      scroll={{ x: 1250 }}
                      locale={{ emptyText: 'В этой подкатегории нет товаров' }}
                    />
                  </Card>
                );
              })
            )}
          </Space>
        </Card>
      </Space>

      <AddProductModal
        open={isAddModalOpen}
        isSaving={isSavingProduct}
        categoryOptions={CATEGORY_ORDER.map((key) => ({
          value: key,
          label: CATEGORY_LABELS[key] ?? key,
        }))}
        onCancel={() => setIsAddModalOpen(false)}
        onSubmit={handleAddProduct}
      />

      <EditProductModal
        open={isEditModalOpen}
        isSaving={isUpdatingProduct}
        categoryOptions={CATEGORY_ORDER.map((key) => ({
          value: key,
          label: CATEGORY_LABELS[key] ?? key,
        }))}
        initialValues={
          editingContext
            ? {
                subcategory: editingContext.sourceSubcategory,
                title: String(editingContext.item.title ?? ''),
                brand: String(editingContext.item.brand ?? ''),
                article: String(editingContext.item.article ?? ''),
                weight: String(editingContext.item.weight ?? ''),
                steps: String(editingContext.item.steps ?? ''),
                price: String(editingContext.item.price ?? ''),
                image: String(editingContext.item.image ?? ''),
                type: String(editingContext.item.type ?? ''),
                sizes: (editingContext.item.sizes ?? []).map((s) => String(s)),
              }
            : null
        }
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingContext(null);
        }}
        onSubmit={handleEditProduct}
      />
    </div>
  );
}

export default AdminDashboard;
